import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Lock, User, Eye, EyeOff, AlertCircle } from 'lucide-react';

// Detecta automaticamente a URL da API baseado no hostname
const getApiBase = () => {
  const hostname = window.location.hostname;
  // Se estiver no domínio do vieiragrill, usa a API de produção
  if (hostname.includes('pdv.hitgrill.com.br')) {
    return 'https://pdv.hitgrill.com.br';
  }
  // Caso contrário, usa a variável de ambiente
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API_BASE = getApiBase();

export default function DriverLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('driver_token');
    if (token) {
      navigate('/driver-app');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/driver-app/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error === 'invalid_credentials' ? 'Usuário ou senha incorretos' : data.error);
      }

      localStorage.setItem('driver_token', data.access_token);
      localStorage.setItem('driver_refresh_token', data.refresh_token);
      localStorage.setItem('driver_info', JSON.stringify(data.me));

      navigate('/driver-app');
    } catch (err) {
      setError(err.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <div className="pt-12 pb-8 px-6 text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-500/30">
          <Truck size={40} className="text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white">Hit Grill</h1>
        <p className="text-slate-400 mt-1">App do Entregador</p>
      </div>

      {/* Login Form */}
      <div className="flex-1 px-6 pb-8">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-6 border border-slate-700/50">
          <h2 className="text-xl font-semibold text-white mb-6 text-center">Entrar</h2>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="driver-login-error">
              <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-slate-400 text-sm mb-2 block">Usuário</label>
              <div className="relative">
                <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl py-4 pl-12 pr-4 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
                  placeholder="Digite seu usuário"
                  required
                  autoComplete="username"
                  data-testid="driver-login-username"
                />
              </div>
            </div>

            <div>
              <label className="text-slate-400 text-sm mb-2 block">Senha</label>
              <div className="relative">
                <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl py-4 pl-12 pr-12 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
                  placeholder="Digite sua senha"
                  required
                  autoComplete="current-password"
                  data-testid="driver-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  data-testid="driver-login-toggle-password"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold py-4 rounded-xl hover:from-orange-600 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-6 shadow-lg shadow-orange-500/30"
              data-testid="driver-login-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Entrando...
                </span>
              ) : (
                'Entrar'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          Problemas para acessar? Fale com o administrador.
        </p>
      </div>

      {/* Footer */}
      <div className="py-4 text-center">
        <p className="text-slate-600 text-xs">Hit Grill © {new Date().getFullYear()}</p>
      </div>
    </div>
  );
}
